import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/** Parse `Content-Disposition` filename (supports RFC 5987 `filename*`). */
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8''|utf-8'')([^;\n]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"(.*)"$/, '$1'));
    } catch {
      return star[1].trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1].trim();
  const unquoted = /filename=([^;\n]+)/i.exec(header);
  return unquoted?.[1]?.trim().replace(/^"(.*)"$/, '$1') ?? null;
}
import { API_BASE_URL } from '../tokens/api-tokens';
import {
  BillingSettingsDetailDto,
  BillingSettingsUpsertDto,
  InvoiceGroupingOption,
  InvoiceItemGroupingOption
} from '../models/billing-settings.model';
import {
  BillingInvoicesMasterClientDto,
  BillingInvoicesSubClientDto,
  ClientInvoiceGroupingMode,
  ClientInvoicesResponseDto,
  InvoiceReconciliationDto
} from '../models/client-invoices.model';
import {
  AddInvoiceLineRequest,
  AddInvoiceLineResult,
  ClientInvoiceDetailResponseDto,
  EligibleInvoiceMemberDto,
  InvoiceActivityDto,
  InvoiceDetailsGroupedDto,
  InvoiceLineCancellationRequest,
  InvoiceSummaryViewDto,
  PortalMemberSearchResult
} from '../models/client-invoice-detail.model';
import { InvoicePaymentViewDto } from '../models/invoice-payment.model';
import {
  PaymentReportApplication,
  PaymentReportCashReceipt,
  PaymentReportFilters,
  PaymentReportInvoice,
  PaymentReportResponse,
  PaymentReportTotals
} from '../models/payment-report.model';
import {
  BillingScheduleCompanyDto,
  BillingScheduleHeaderDto,
  BillingScheduleProductDto,
  BillingScheduleResponseDto
} from '../models/billing-schedules.model';
import { pick, pickDateStr, pickNum, pickStr } from '../utils/api-response-pick.util';
import { parseClientInvoiceGroupingMode } from '../utils/invoice-grouping-mode';

function normInvoiceGrouping(raw: unknown): InvoiceGroupingOption {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    invoiceGroupingOptionId: Number(o['invoiceGroupingOptionId'] ?? o['InvoiceGroupingOptionId'] ?? 0),
    name: (o['name'] ?? o['Name']) as string | null | undefined,
    active: (o['active'] ?? o['Active']) as boolean | null | undefined
  };
}

function normInvoiceItemGrouping(raw: unknown): InvoiceItemGroupingOption {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    invoiceItemGroupingOptionId: Number(
      o['invoiceItemGroupingOptionId'] ?? o['InvoiceItemGroupingOptionId'] ?? 0
    ),
    name: (o['name'] ?? o['Name']) as string | null | undefined,
    active: (o['active'] ?? o['Active']) as boolean | null | undefined
  };
}

/** Unwrap gateway envelopes like `{ data: { ... } }` when present. */
function extractBillingSettingsPayload(raw: unknown): unknown {
  if (raw == null || typeof raw !== 'object') {
    return raw;
  }
  const r = raw as Record<string, unknown>;
  const data = r['data'] ?? r['Data'];
  if (data != null && typeof data === 'object') {
    return data;
  }
  return raw;
}

function normReconciliation(raw: unknown): InvoiceReconciliationDto | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const viewCount = Number(pick(o, 'viewCount', 'ViewCount') ?? 0) || 0;
  return {
    isViewed: Boolean(pick(o, 'isViewed', 'IsViewed') ?? viewCount > 0),
    isViewedByClient: Boolean(pick(o, 'isViewedByClient', 'IsViewedByClient') ?? false),
    viewCount,
    lastViewedAt: pickDateStr(o, 'lastViewedAt', 'LastViewedAt'),
    lastViewedBy: pickStr(o, 'lastViewedBy', 'LastViewedBy'),
    isFinal: Boolean(pick(o, 'isFinal', 'IsFinal') ?? false),
    approvedAt: pickDateStr(o, 'approvedAt', 'ApprovedAt'),
    approvedBy: pickStr(o, 'approvedBy', 'ApprovedBy'),
    approvalType: (pickStr(o, 'approvalType', 'ApprovalType') as InvoiceReconciliationDto['approvalType']) ?? null
  };
}

function normSubClientRow(raw: unknown): BillingInvoicesSubClientDto {
  const o = (raw ?? {}) as Record<string, unknown>;
  const rec = pick<unknown>(o, 'reconciliation', 'Reconciliation');
  return {
    cCode: String(pick(o, 'cCode', 'CCode') ?? ''),
    cName: pickStr(o, 'cName', 'CName'),
    invoiceId: pickNum(o, 'invoiceId', 'InvoiceId'),
    invDate: pickDateStr(o, 'invDate', 'InvDate'),
    dueDate: pickDateStr(o, 'dueDate', 'DueDate'),
    invoiceCount: Number(pick(o, 'invoiceCount', 'InvoiceCount') ?? 0) || 0,
    totalAmount: pickNum(o, 'totalAmount', 'TotalAmount'),
    amountPaid: pickNum(o, 'amountPaid', 'AmountPaid'),
    amountDue: pickNum(o, 'amountDue', 'AmountDue'),
    billingStatus: pickStr(o, 'billingStatus', 'BillingStatus'),
    invoiceStatus: pickStr(o, 'invoiceStatus', 'InvoiceStatus'),
    reconciliation: rec != null ? normReconciliation(rec) : null,
    purchaseOrder: pickStr(o, 'purchaseOrder', 'PurchaseOrder')
  };
}

function normMasterClientRow(raw: unknown): BillingInvoicesMasterClientDto {
  const o = (raw ?? {}) as Record<string, unknown>;
  const rec = pick<unknown>(o, 'reconciliation', 'Reconciliation');
  return {
    mCode: String(pick(o, 'mCode', 'MCode') ?? ''),
    mName: pickStr(o, 'mName', 'MName'),
    invoiceId: pickNum(o, 'invoiceId', 'InvoiceId'),
    invDate: pickDateStr(o, 'invDate', 'InvDate'),
    dueDate: pickDateStr(o, 'dueDate', 'DueDate'),
    invoiceCount: Number(pick(o, 'invoiceCount', 'InvoiceCount') ?? 0) || 0,
    totalAmount: pickNum(o, 'totalAmount', 'TotalAmount'),
    amountPaid: pickNum(o, 'amountPaid', 'AmountPaid'),
    amountDue: pickNum(o, 'amountDue', 'AmountDue'),
    paidInvoiceCount: Number(pick(o, 'paidInvoiceCount', 'PaidInvoiceCount') ?? 0) || 0,
    openInvoiceCount: Number(pick(o, 'openInvoiceCount', 'OpenInvoiceCount') ?? 0) || 0,
    dueInvoiceCount: Number(pick(o, 'dueInvoiceCount', 'DueInvoiceCount') ?? 0) || 0,
    overdueInvoiceCount: Number(pick(o, 'overdueInvoiceCount', 'OverdueInvoiceCount') ?? 0) || 0,
    invoiceStatus: pickStr(o, 'invoiceStatus', 'InvoiceStatus'),
    reconciliation: rec != null ? normReconciliation(rec) : null,
    purchaseOrder: pickStr(o, 'purchaseOrder', 'PurchaseOrder')
  };
}

function parseGroupingMode(raw: unknown): ClientInvoiceGroupingMode | null {
  return parseClientInvoiceGroupingMode(raw);
}

function normalizeClientInvoiceDetail(raw: unknown): ClientInvoiceDetailResponseDto {
  const extracted = extractBillingSettingsPayload(raw);
  const dto = (extracted ?? {}) as ClientInvoiceDetailResponseDto;
  if (extracted == null || typeof extracted !== 'object') {
    return dto;
  }
  const r = extracted as Record<string, unknown>;
  const item = pickStr(r, 'itemGroupingOption', 'ItemGroupingOption');
  const groupingMode = parseGroupingMode(pick(r, 'groupingMode', 'GroupingMode'));
  const invoiceCountRaw = pick<number | string | null>(r, 'invoiceCount', 'InvoiceCount');
  const invoiceCount =
    invoiceCountRaw != null && invoiceCountRaw !== '' ? Number(invoiceCountRaw) : undefined;
  const isGroupedRaw = pick<boolean | null>(r, 'isGrouped', 'IsGrouped');
  return {
    ...dto,
    ...(item ? { itemGroupingOption: item } : {}),
    groupingMode: groupingMode ?? dto.groupingMode ?? null,
    invoiceCount: Number.isFinite(invoiceCount) ? invoiceCount : dto.invoiceCount,
    isGrouped: isGroupedRaw === true || (groupingMode === 'MasterClient' && (invoiceCount ?? 0) > 1)
  };
}

function normalizeClientInvoicesResponse(raw: unknown): ClientInvoicesResponseDto {
  const extracted = extractBillingSettingsPayload(raw);
  if (extracted == null || typeof extracted !== 'object') {
    return { groupingMode: null, subClientInvoices: [], masterClientInvoices: [] };
  }
  const r = extracted as Record<string, unknown>;
  const subList = pick<unknown[]>(r, 'subClientInvoices', 'SubClientInvoices');
  const masterList = pick<unknown[]>(r, 'masterClientInvoices', 'MasterClientInvoices');
  return {
    groupingMode: parseGroupingMode(pick(r, 'groupingMode', 'GroupingMode')),
    subClientInvoices: Array.isArray(subList) ? subList.map(normSubClientRow) : [],
    masterClientInvoices: Array.isArray(masterList) ? masterList.map(normMasterClientRow) : []
  };
}

function normalizeBillingSettingsDetail(raw: unknown): BillingSettingsDetailDto {
  const extracted = extractBillingSettingsPayload(raw);
  if (extracted == null || typeof extracted !== 'object') {
    return {
      mCode: '',
      invoiceGroupingOptionId: 0,
      invoiceItemGroupingOptionId: 0,
      invoiceGroupingOption: { invoiceGroupingOptionId: 0, name: null, active: null },
      invoiceItemGroupingOption: { invoiceItemGroupingOptionId: 0, name: null, active: null },
      displayEmployeeIdOnInvoice: false
    };
  }
  const r = extracted as Record<string, unknown>;
  const igOpt = pick<unknown>(r, 'invoiceGroupingOption', 'InvoiceGroupingOption');
  const iiOpt = pick<unknown>(r, 'invoiceItemGroupingOption', 'InvoiceItemGroupingOption');
  const igList = pick<unknown[]>(r, 'invoiceGroupingOptions', 'InvoiceGroupingOptions');
  const iiList = pick<unknown[]>(r, 'invoiceItemGroupingOptions', 'InvoiceItemGroupingOptions');
  const invG = normInvoiceGrouping(igOpt);
  const itemG = normInvoiceItemGrouping(iiOpt);

  const rawDisplay =
    pick<boolean | null>(r, 'displayEmployeeIdOnInvoice', 'DisplayEmployeeIdOnInvoice') ??
    pick<boolean | null>(r, 'employeeIdOnInvoice', 'EmployeeIdOnInvoice');

  return {
    mCode: String(pick(r, 'mCode', 'MCode') ?? ''),
    invoiceGroupingOptionId: Number(pick(r, 'invoiceGroupingOptionId', 'InvoiceGroupingOptionId') ?? invG.invoiceGroupingOptionId),
    invoiceItemGroupingOptionId: Number(
      pick(r, 'invoiceItemGroupingOptionId', 'InvoiceItemGroupingOptionId') ?? itemG.invoiceItemGroupingOptionId
    ),
    invoiceGroupingOption: invG,
    invoiceItemGroupingOption: itemG,
    invoiceGroupingOptions: Array.isArray(igList) ? igList.map(normInvoiceGrouping) : undefined,
    invoiceItemGroupingOptions: Array.isArray(iiList) ? iiList.map(normInvoiceItemGrouping) : undefined,
    displayEmployeeIdOnInvoice: Boolean(rawDisplay ?? false)
  };
}

function normalizeBillingScheduleCompany(raw: unknown): BillingScheduleCompanyDto | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const cCode = pickStr(o, 'cCode', 'CCode');
  if (!cCode) return null;
  return {
    cCode,
    name: pickStr(o, 'name', 'Name')
  };
}

function normalizeBillingScheduleHeader(raw: unknown): BillingScheduleHeaderDto | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const billingSchedule = pickStr(o, 'billingSchedule', 'BillingSchedule');
  const description = pickStr(o, 'description', 'Description');
  const whenPay = pickStr(o, 'whenPay', 'WhenPay');
  const cisStartDate = pickDateStr(o, 'cisStartDate', 'CisStartDate');
  if (!billingSchedule && !description && !whenPay && !cisStartDate) return null;
  return {
    billingSchedule,
    description,
    whenPay,
    cisStartDate
  };
}

function normalizeBillingScheduleProduct(raw: unknown): BillingScheduleProductDto | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const productName = pickStr(o, 'productName', 'ProductName');
  if (!productName) return null;
  return {
    productName,
    amount: pickNum(o, 'amount', 'Amount'),
    monthlyRate: pickNum(o, 'monthlyRate', 'MonthlyRate'),
    invoiceDate: pickDateStr(o, 'invoiceDate', 'InvoiceDate'),
    coverageDate: pickDateStr(o, 'coverageDate', 'CoverageDate'),
    nextCoverageDate: pickDateStr(o, 'nextCoverageDate', 'NextCoverageDate'),
    minInvoiceDate: pickDateStr(o, 'minInvoiceDate', 'MinInvoiceDate'),
    maxInvoiceDate: pickDateStr(o, 'maxInvoiceDate', 'MaxInvoiceDate'),
    isLatest: Boolean(pick<boolean>(o, 'isLatest', 'IsLatest') ?? false)
  };
}

function normalizeBillingScheduleResponse(raw: unknown): BillingScheduleResponseDto {
  const extracted = extractBillingSettingsPayload(raw);
  if (extracted == null || typeof extracted !== 'object') {
    return {
      mCode: '',
      selectedCCode: '',
      companies: [],
      header: null,
      products: [],
      isConfigured: false
    };
  }
  const o = extracted as Record<string, unknown>;
  const companies = pick<unknown[]>(o, 'companies', 'Companies');
  const products = pick<unknown[]>(o, 'products', 'Products');
  return {
    mCode: String(pick(o, 'mCode', 'MCode') ?? ''),
    selectedCCode: String(pick(o, 'selectedCCode', 'SelectedCCode') ?? ''),
    companies: Array.isArray(companies)
      ? companies
          .map(normalizeBillingScheduleCompany)
          .filter((value): value is BillingScheduleCompanyDto => value != null)
      : [],
    header: normalizeBillingScheduleHeader(pick(o, 'header', 'Header')),
    products: Array.isArray(products)
      ? products
          .map(normalizeBillingScheduleProduct)
          .filter((value): value is BillingScheduleProductDto => value != null)
      : [],
    isConfigured: Boolean(pick<boolean>(o, 'isConfigured', 'IsConfigured') ?? false)
  };
}

function normPaymentReportApplication(raw: unknown): PaymentReportApplication {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    invoiceId: Number(pick(o, 'invoiceId', 'InvoiceId') ?? 0),
    invoiceNumber: pickStr(o, 'invoiceNumber', 'InvoiceNumber'),
    invoiceDate: pickDateStr(o, 'invoiceDate', 'InvoiceDate'),
    amountApplied: Number(pick(o, 'amountApplied', 'AmountApplied') ?? 0) || 0
  };
}

function normPaymentReportCashReceipt(raw: unknown): PaymentReportCashReceipt {
  const o = (raw ?? {}) as Record<string, unknown>;
  const apps = pick<unknown[]>(o, 'applications', 'Applications');
  return {
    receiptId: pickNum(o, 'receiptId', 'ReceiptId'),
    paymentFrom: pickStr(o, 'paymentFrom', 'PaymentFrom'),
    received: pickDateStr(o, 'received', 'Received'),
    cashReceiptType: pickStr(o, 'cashReceiptType', 'CashReceiptType'),
    checkWireNumber: pickStr(o, 'checkWireNumber', 'CheckWireNumber'),
    amount: Number(pick(o, 'amount', 'Amount') ?? 0) || 0,
    netCheckAmount: Number(pick(o, 'netCheckAmount', 'NetCheckAmount') ?? 0) || 0,
    applications: Array.isArray(apps) ? apps.map(normPaymentReportApplication) : []
  };
}

function normPaymentReportInvoice(raw: unknown): PaymentReportInvoice {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    invoiceId: Number(pick(o, 'invoiceId', 'InvoiceId') ?? 0),
    invoiceNumber: pickStr(o, 'invoiceNumber', 'InvoiceNumber'),
    invoiceDate: pickDateStr(o, 'invoiceDate', 'InvoiceDate'),
    product: pickStr(o, 'product', 'Product'),
    employeeCount: Number(pick(o, 'employeeCount', 'EmployeeCount') ?? 0) || 0,
    invoicedAmount: Number(pick(o, 'invoicedAmount', 'InvoicedAmount') ?? 0) || 0,
    paidAmount: Number(pick(o, 'paidAmount', 'PaidAmount') ?? 0) || 0,
    balanceDue: Number(pick(o, 'balanceDue', 'BalanceDue') ?? 0) || 0
  };
}

function normPaymentReportTotals(raw: unknown): PaymentReportTotals {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    totalCashReceived: Number(pick(o, 'totalCashReceived', 'TotalCashReceived') ?? 0) || 0,
    totalInvoicedAmount: Number(pick(o, 'totalInvoicedAmount', 'TotalInvoicedAmount') ?? 0) || 0,
    totalPaidAmount: Number(pick(o, 'totalPaidAmount', 'TotalPaidAmount') ?? 0) || 0,
    totalBalanceDue: Number(pick(o, 'totalBalanceDue', 'TotalBalanceDue') ?? 0) || 0
  };
}

function normalizePaymentReportResponse(raw: unknown): PaymentReportResponse {
  const extracted = extractBillingSettingsPayload(raw);
  if (extracted == null || typeof extracted !== 'object') {
    return {
      cashReceipts: [],
      invoices: [],
      totals: {
        totalCashReceived: 0,
        totalInvoicedAmount: 0,
        totalPaidAmount: 0,
        totalBalanceDue: 0
      }
    };
  }
  const o = extracted as Record<string, unknown>;
  const cashReceipts = pick<unknown[]>(o, 'cashReceipts', 'CashReceipts');
  const invoices = pick<unknown[]>(o, 'invoices', 'Invoices');
  const totals = pick<unknown>(o, 'totals', 'Totals');
  return {
    cashReceipts: Array.isArray(cashReceipts) ? cashReceipts.map(normPaymentReportCashReceipt) : [],
    invoices: Array.isArray(invoices) ? invoices.map(normPaymentReportInvoice) : [],
    totals: normPaymentReportTotals(totals)
  };
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_BASE_URL);

  getBillingSettings(mcode: string): Observable<BillingSettingsDetailDto> {
    return this.http.get<unknown>(`${this.apiUrl}/client/${mcode}/billing/settings`).pipe(
      map(raw => normalizeBillingSettingsDetail(raw))
    );
  }

  private billingSettingsRequestBody(body: BillingSettingsUpsertDto): BillingSettingsUpsertDto {
    return {
      invoiceGroupingOptionId: body.invoiceGroupingOptionId,
      invoiceItemGroupingOptionId: body.invoiceItemGroupingOptionId,
      displayEmployeeIdOnInvoice: body.displayEmployeeIdOnInvoice ?? null
    };
  }

  /** Create billing settings when none exist for the client. */
  createBillingSettings(mcode: string, body: BillingSettingsUpsertDto): Observable<BillingSettingsDetailDto> {
    return this.http
      .post<unknown>(`${this.apiUrl}/client/${mcode}/billing/settings`, this.billingSettingsRequestBody(body))
      .pipe(map(normalizeBillingSettingsDetail));
  }

  /** Update existing billing settings. */
  updateBillingSettings(mcode: string, body: BillingSettingsUpsertDto): Observable<BillingSettingsDetailDto> {
    return this.http
      .put<unknown>(`${this.apiUrl}/client/${mcode}/billing/settings`, this.billingSettingsRequestBody(body))
      .pipe(map(normalizeBillingSettingsDetail));
  }

  /**
   * Invoice register for the client. Optional `billingStatus` (Paid | Open | Due | Past Due) when the gateway should filter;
   * the HR portal loads the full set and filters in the UI unless you pass a value here.
   */
  getClientInvoices(mcode: string, billingStatus?: string): Observable<ClientInvoicesResponseDto> {
    const path = `${this.apiUrl}/client/${encodeURIComponent(mcode)}/invoices`;
    const url =
      billingStatus != null && billingStatus !== ''
        ? `${path}?billingStatus=${encodeURIComponent(billingStatus)}`
        : path;
    return this.http.get<unknown>(url).pipe(map(normalizeClientInvoicesResponse));
  }

  getBillingSchedules(mcode: string, ccode?: string): Observable<BillingScheduleResponseDto> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const query = ccode != null && ccode.trim() !== '' ? `?ccode=${encodeURIComponent(ccode.trim())}` : '';
    const url = `${base}/client/${encodeURIComponent(mcode)}/billing/schedules${query}`;
    return this.http.get<unknown>(url).pipe(map(normalizeBillingScheduleResponse));
  }

  /**
   * GET /client/{mcode}/invoices/{invoiceId}
   */
  getClientInvoiceDetail(mcode: string, invoiceId: string): Observable<ClientInvoiceDetailResponseDto> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(invoiceId)}`;
    return this.http.get<unknown>(url).pipe(map(normalizeClientInvoiceDetail));
  }

  /**
   * GET /client/{mcode}/invoices/{invoiceId}/payments
   */
  getClientInvoicePayments(mcode: string, invoiceId: string): Observable<InvoicePaymentViewDto[]> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(invoiceId)}/payments`;
    return this.http.get<InvoicePaymentViewDto[]>(url);
  }

  /**
   * GET /client/{mcode}/invoices/{invoiceId}/pdf — file stream (PDF).
   */
  exportClientInvoicePdf(
    mcode: string,
    invoiceId: string
  ): Observable<{ blob: Blob; filename: string }> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(invoiceId)}/pdf`;
    const fallback = `invoice-${invoiceId}.pdf`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map(res => ({
        blob: res.body as Blob,
        filename: filenameFromContentDisposition(res.headers.get('content-disposition')) ?? fallback
      }))
    );
  }

  /**
   * GET /client/{mcode}/invoices/{invoiceId}/excel — file stream (Excel).
   */
  exportClientInvoiceXlsx(
    mcode: string,
    invoiceId: string
  ): Observable<{ blob: Blob; filename: string }> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(invoiceId)}/excel`;
    const fallback = `invoice-${invoiceId}.xlsx`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map(res => ({
        blob: res.body as Blob,
        filename: filenameFromContentDisposition(res.headers.get('content-disposition')) ?? fallback
      }))
    );
  }

  // ── Reconciliation ───────────────────────────────────────────────────────

  /**
   * POST /client/{mcode}/invoices/{invoiceId}/reconciliation/viewed
   * Registers a view event for this page navigation. Called once per page open.
   */
  registerInvoiceView(mcode: string, invoiceId: string | number): Observable<InvoiceReconciliationDto> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(String(invoiceId))}/reconciliation/viewed`;
    return this.http.post<InvoiceReconciliationDto>(url, null);
  }

  /** @deprecated use registerInvoiceView */
  markInvoiceViewed(mcode: string, invoiceId: string | number): Observable<InvoiceReconciliationDto> {
    return this.registerInvoiceView(mcode, invoiceId);
  }

  /**
   * POST /client/{mcode}/invoices/{invoiceId}/reconciliation/approve
   * Manually approves and finalizes the invoice.
   */
  approveInvoice(mcode: string, invoiceId: string | number): Observable<InvoiceReconciliationDto> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(String(invoiceId))}/reconciliation/approve`;
    return this.http.post<InvoiceReconciliationDto>(url, null);
  }

  /**
   * GET /client/{mcode}/invoices/{invoiceId}/activities
   * Returns the ordered activity timeline for the invoice.
   */
  getInvoiceActivities(mcode: string, invoiceId: string | number): Observable<InvoiceActivityDto[]> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(String(invoiceId))}/activities`;
    return this.http.get<InvoiceActivityDto[]>(url);
  }

  /**
   * GET /client/{mcode}/invoices/{invoiceId}/eligible-members?searchTerm=…
   * Returns members whose active member products cover the invoice billing month and are
   * not already on the invoice. Eligibility is enforced server-side.
   * Requires CompanyBilling.Manage.
   */
  getEligibleMembers(
    mcode: string,
    invoiceId: string | number,
    searchTerm?: string
  ): Observable<EligibleInvoiceMemberDto[]> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const term = searchTerm?.trim() ?? '';
    const qs = term ? `?searchTerm=${encodeURIComponent(term)}` : '';
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(String(invoiceId))}/eligible-members${qs}`;
    return this.http.get<EligibleInvoiceMemberDto[]>(url);
  }

  /**
   * POST /client/{mcode}/invoices/{invoiceId}/lines
   * Adds a single invoice line for an existing active member product.
   * Does NOT create a member product or regenerate the full invoice.
   * Body needs subId + memberProductId; effectiveDate is optional and omitted by the portal.
   * Requires CompanyBilling.Manage.
   */
  addInvoiceLine(
    mcode: string,
    invoiceId: string | number,
    body: AddInvoiceLineRequest
  ): Observable<AddInvoiceLineResult> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(String(invoiceId))}/lines`;
    return this.http.post<AddInvoiceLineResult>(url, body);
  }

  /**
   * POST /client/{mcode}/invoices/{invoiceId}/lines/cancel
   * Cancels one or more invoice lines: cancels the associated member products and zeroes
   * out the invoice line amounts. Requires Invoices.Manage — Enrollment.Manage is NOT needed.
   */
  cancelInvoiceLines(
    mcode: string,
    invoiceId: string | number,
    body: InvoiceLineCancellationRequest
  ): Observable<void> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${encodeURIComponent(String(invoiceId))}/lines/cancel`;
    return this.http.post<void>(url, body);
  }

  /**
   * PATCH /client/{mcode}/invoices/{invoiceId}/purchase-order
   */
  updateInvoicePurchaseOrder(
    mcode: string,
    invoiceId: number,
    purchaseOrder: string | null
  ): Observable<void> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const url = `${base}/client/${encodeURIComponent(mcode)}/invoices/${invoiceId}/purchase-order`;
    return this.http.patch<void>(url, { purchaseOrder });
  }

  getPaymentReport(mcode: string, filters: PaymentReportFilters): Observable<PaymentReportResponse> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const query = new URLSearchParams();
    if (filters.group) query.set('group', filters.group);
    if (filters.fromDate) query.set('fromDate', filters.fromDate);
    if (filters.toDate) query.set('toDate', filters.toDate);
    if (filters.includeApplications != null) {
      query.set('includeApplications', String(filters.includeApplications));
    }

    const qs = query.toString();
    const url = `${base}/client/${encodeURIComponent(mcode)}/payment-report${qs ? `?${qs}` : ''}`;
    return this.http.get<unknown>(url).pipe(map(normalizePaymentReportResponse));
  }

  /**
   * GET /client/{mcode}/payment-report/pdf — file stream (PDF).
   */
  exportPaymentReportPdf(
    mcode: string,
    filters: PaymentReportFilters
  ): Observable<{ blob: Blob; filename: string }> {
    const base = this.apiUrl.replace(/\/+$/, '');
    const query = new URLSearchParams();
    if (filters.group) query.set('group', filters.group);
    if (filters.fromDate) query.set('fromDate', filters.fromDate);
    if (filters.toDate) query.set('toDate', filters.toDate);
    const qs = query.toString();

    const url = `${base}/client/${encodeURIComponent(mcode)}/payment-report/pdf${qs ? `?${qs}` : ''}`;
    const fallback = `payment-report-${mcode}.pdf`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map(res => ({
        blob: res.body as Blob,
        filename: filenameFromContentDisposition(res.headers.get('content-disposition')) ?? fallback
      }))
    );
  }
}
